import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { API } from "../../config";
import Backdrop from "../Catalogs/Dialog/Backdrop";
import Spin from "../Spinner/Spinner";
import styles from "../Users/Carts/CreateCartDialog.module.css";

const LIMIT = 10;

export const AddKitToCartDialog = ({
  show = false,
  onClose = () => {},
  kit,
  onSuccess = () => {},
}) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingCarts, setLoadingCarts] = useState(false);
  const [selectedCartId, setSelectedCartId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userDropdownRef = useRef(null);

  useEffect(() => {
    if (!show) {
      setSearch("");
      setDebouncedSearch("");
      setUsers([]);
      setSelectedUser(null);
      setUserDetails(null);
      setSelectedCartId("");
      setShowUserDropdown(false);
    }
  }, [show]);

  useEffect(() => {
    if (!show) return;
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [search, show]);

  useEffect(() => {
    if (!show) return;
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/Admin/users`, {
          params: {
            page: 1,
            limit: LIMIT,
            search: debouncedSearch || undefined,
          },
        });
        setUsers(response.data.users || []);
      } catch (error) {
        console.error("Failed to fetch users:", error);
        toast.error("Failed to fetch users");
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [debouncedSearch, show]);

  useEffect(() => {
    if (!selectedUser?._id || !show) return;
    const fetchUserDetails = async () => {
      setLoadingCarts(true);
      try {
        const response = await axios.get(
          `${API}/Admin/users/${selectedUser._id}`,
        );
        setUserDetails(response.data);
        setSelectedCartId("");
      } catch (error) {
        console.error("Failed to fetch user carts:", error);
        toast.error("Failed to fetch user carts");
      } finally {
        setLoadingCarts(false);
      }
    };
    fetchUserDetails();
  }, [selectedUser?._id, show]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(e.target)
      ) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const carts = userDetails?.carts ?? [];

  const handleAddToCart = async () => {
    if (!selectedCartId) {
      toast.error("Please select a cart");
      return;
    }
    if (!kit?.products?.length) {
      toast.error("No products in kit");
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/Admin/carts/${selectedCartId}/products`,
        {
          products: kit.products.map((p) => ({
            productId: p.productId,
            quantity: p.quantity ?? 1,
          })),
        },
      );
      if (response.status === 201 || response.status === 200) {
        toast.success("Products added to cart successfully");
        onSuccess();
        onClose();
        navigate(`/users/${selectedUser._id}/carts/${selectedCartId}`);
      } else {
        toast.error("Failed to add products to cart");
      }
    } catch (error) {
      console.error("Failed to add to cart:", error);
      toast.error(
        error.response?.data?.message || "Failed to add products to cart",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (!show) return null;

  return (
    <div>
      <Backdrop show={show} switch={handleClose} />
      <div
        className={styles.popup}
        style={{
          width: 520,
          height: "auto",
          maxHeight: "90vh",
          overflow: "visible",
        }}
      >
        <div className={styles.header} style={{ marginBottom: 8 }}>
          <h2 className={styles.title}>Add Kit to Cart</h2>
        </div>

        <div className={styles.content} style={{ marginTop: 0 }}>
          <div className={styles.column} style={{ overflow: "visible" }}>
            <label className={styles.columnHeader} style={{ marginBottom: 4 }}>
              Select user
            </label>
            <div ref={userDropdownRef} style={{ position: "relative" }}>
              {selectedUser ? (
                <div
                  className={styles.searchInput}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "default",
                  }}
                >
                  <span
                    style={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {selectedUser.name || "-"} ({selectedUser.email || "-"})
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedUser(null);
                      setSearch("");
                      setUserDetails(null);
                      setSelectedCartId("");
                    }}
                    style={{
                      flexShrink: 0,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 18,
                      color: "#666",
                      padding: "0 4px",
                      lineHeight: 1,
                      margin: 0,
                      width: "fit-content",
                    }}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder="Type to search users..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setShowUserDropdown(true);
                  }}
                  onFocus={() => setShowUserDropdown(true)}
                />
              )}
              {showUserDropdown && !selectedUser && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    marginTop: 4,
                    maxHeight: 240,
                    zIndex: 1000,
                    borderRadius: 12,
                    backgroundColor: "#fff",
                    padding: 8,
                    overflowY: "auto",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  {loading ? (
                    <div className={styles.spinnerWrapper}>
                      <Spin />
                    </div>
                  ) : users.length ? (
                    users.map((user) => (
                      <button
                        key={user._id}
                        type="button"
                        className={styles.productRow}
                        onClick={() => {
                          setSelectedUser(user);
                          setSearch("");
                          setShowUserDropdown(false);
                        }}
                      >
                        <div className={styles.productInfo}>
                          <div className={styles.productName}>
                            {user?.name || "-"}
                          </div>
                          <div className={styles.productMeta}>
                            {user?.companyName || "-"}
                          </div>
                          <div className={styles.productMeta}>
                            {user?.email || "-"}
                          </div>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className={styles.emptyState}>No users found</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {selectedUser && (
            <div className={styles.column}>
              <label
                className={styles.columnHeader}
                style={{ marginBottom: 4 }}
              >
                Select cart
              </label>
              {loadingCarts ? (
                <div
                  className={styles.spinnerWrapper}
                  style={{ minHeight: 80 }}
                >
                  <Spin />
                </div>
              ) : (
                <select
                  className={styles.searchInput}
                  value={selectedCartId}
                  onChange={(e) => setSelectedCartId(e.target.value)}
                >
                  <option value="">Choose a cart...</option>
                  {carts.map((cart) => (
                    <option key={cart._id} value={cart._id}>
                      {cart?.name || userDetails?.user?.companyName || "Cart"} (
                      {cart?.products?.length ?? 0} items)
                    </option>
                  ))}
                  {carts.length === 0 && (
                    <option value="" disabled>
                      No carts found for this user
                    </option>
                  )}
                </select>
              )}
            </div>
          )}

          <div className={styles.footer}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.confirmButton}
              onClick={handleAddToCart}
              disabled={!selectedCartId || submitting}
            >
              {submitting ? "Adding..." : "Add to Cart"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
